import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaExclamationTriangle, FaInfo, FaTimes, FaBell } from 'react-icons/fa';

// ==================== TIPOS ====================

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

// ==================== CONTEXTO ====================

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ==================== PROVEEDOR ====================

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      duration: notification.duration || 5000, // 5 segundos por defecto
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// ==================== HOOK ====================

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// ==================== COMPONENTE DE NOTIFICACIONES ====================

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <FaCheck className="text-green-400" />;
      case 'error':
        return <FaExclamationTriangle className="text-red-400" />;
      case 'warning':
        return <FaExclamationTriangle className="text-yellow-400" />;
      case 'info':
        return <FaInfo className="text-blue-400" />;
      default:
        return <FaBell className="text-gray-400" />;
    }
  };

  const getNotificationStyles = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-green-900/50 border-green-500/50 text-green-100';
      case 'error':
        return 'bg-red-900/50 border-red-500/50 text-red-100';
      case 'warning':
        return 'bg-yellow-900/50 border-yellow-500/50 text-yellow-100';
      case 'info':
        return 'bg-blue-900/50 border-blue-500/50 text-blue-100';
      default:
        return 'bg-gray-900/50 border-gray-500/50 text-gray-100';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full">
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={`${getNotificationStyles(notification.type)} backdrop-blur-xl rounded-xl p-4 border shadow-lg`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getNotificationIcon(notification.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">
                  {notification.title}
                </h4>
                <p className="text-sm opacity-90 leading-relaxed">
                  {notification.message}
                </p>
                
                {notification.action && (
                  <button
                    onClick={notification.action.onClick}
                    className="mt-2 text-xs font-medium underline hover:no-underline transition-all duration-200"
                  >
                    {notification.action.label}
                  </button>
                )}
              </div>
              
              <button
                onClick={() => removeNotification(notification.id)}
                className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors duration-200"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

// ==================== UTILIDADES ====================

// Funciones helper para crear notificaciones comunes
export const useNotificationHelpers = () => {
  const { addNotification } = useNotifications();

  const showSuccess = useCallback((title: string, message: string, action?: Notification['action']) => {
    addNotification({
      type: 'success',
      title,
      message,
      action,
      duration: 4000,
    });
  }, [addNotification]);

  const showError = useCallback((title: string, message: string, action?: Notification['action']) => {
    addNotification({
      type: 'error',
      title,
      message,
      action,
      duration: 6000, // Errores se muestran más tiempo
    });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message: string, action?: Notification['action']) => {
    addNotification({
      type: 'warning',
      title,
      message,
      action,
      duration: 5000,
    });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message: string, action?: Notification['action']) => {
    addNotification({
      type: 'info',
      title,
      message,
      action,
      duration: 4000,
    });
  }, [addNotification]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
